import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X, Camera, TrashSimple, Eye, PaperPlaneRight, BookmarkSimple, PaperPlaneTilt, SpeakerHigh, SpeakerSlash, FolderSimpleStar, Star, Storefront, CaretLeft, CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAPBACKS = ["❤️", "👍", "👎", "😂"];

// Sentinel id used to bucket every "as store" story into ONE ring — shared
// across all crew so a story posted by Dom AS the store and a story posted
// by Josue AS the store land in the same bubble on the home row, NOT in
// each of their personal buckets. The real poster is preserved on each
// story via `posted_by_id` for audit / delete ownership. (user report
// 2026-05-04 "why didn't it post under its own bubble".)
const STORE_GROUP_ID = "__store__";

// Group stories by author so each crew member gets a single ring in the row
// (even if they posted multiple stories back-to-back). Store-persona
// stories are bucketed separately so they never blend into the real
// author's personal ring.
const groupByAuthor = (stories) => {
  const map = new Map();
  stories.forEach((s) => {
    const key = s.as_store ? STORE_GROUP_ID : s.author_id;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  });
  return Array.from(map.entries()).map(([authorId, items]) => ({
    author_id: authorId,
    author_name: items[0].author_name,
    author_photo: items[0].author_photo,
    stories: items.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    is_store: authorId === STORE_GROUP_ID,
  }));
};

const initialsOf = (name = "") => (name.trim().charAt(0) || "?").toUpperCase();

// Client-side resize → JPEG Blob so we don't ship a 5MB phone photo over the
// wire. Matches profile-photo compression budget. Returns {blob, previewUrl}
// where previewUrl is a short-lived object URL the composer uses for the
// preview image (much lighter than base64 for memory-constrained iPhones).
const compressImage = (file, maxDim = 900) => new Promise((resolve, reject) => {
  if (!file) return reject(new Error("No file"));
  if (file.size > 12 * 1024 * 1024) return reject(new Error("Image over 12MB"));
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Compression failed"));
          resolve({ blob, previewUrl: URL.createObjectURL(blob) });
        },
        "image/jpeg",
        0.82,
      );
    };
    img.onerror = () => reject(new Error("Image read failed"));
    img.src = reader.result;
  };
  reader.onerror = () => reject(new Error("Read failed"));
  reader.readAsDataURL(file);
});

// Video: we don't re-encode client-side (too slow), so we gate by duration +
// raw file size. 10s duration + 11MB raw cap (base64-inflated stays ≤15MB,
// which is the MongoDB doc limit the server enforces). Returns {blob, previewUrl}
// where blob is the original File and previewUrl is a cheap object URL for
// the composer preview (avoids the iPhone-crashing base64 round-trip).
const MAX_VIDEO_SECONDS = 10;
const MAX_VIDEO_BYTES = 11 * 1024 * 1024; // 11 MB raw → ~15 MB base64 encoded
const validateVideo = (file) => new Promise((resolve, reject) => {
  if (!file) return reject(new Error("No file"));
  if (file.size > MAX_VIDEO_BYTES) return reject(new Error("Video too big — try a shorter clip (under 10 seconds) or lower resolution"));
  // Probe duration via a hidden <video> before we commit to uploading.
  const url = URL.createObjectURL(file);
  const vid = document.createElement("video");
  vid.preload = "metadata";
  vid.onloadedmetadata = () => {
    if (vid.duration > MAX_VIDEO_SECONDS + 0.2) {
      URL.revokeObjectURL(url);
      return reject(new Error(`Video is ${vid.duration.toFixed(1)}s — keep it under ${MAX_VIDEO_SECONDS}s`));
    }
    // Keep the object URL alive for the preview — the composer revokes it
    // when it unmounts / swaps media.
    resolve({ blob: file, previewUrl: url });
  };
  vid.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error("Not a valid video"));
  };
  vid.src = url;
});

export default function StoriesRow({ user, isManager, enabled = true, composerOpen: externalComposerOpen, onComposerOpenChange }) {
  // Posting is restricted at non-3094 stores (user spec 2026-05-23):
  //   - 3094 crew → everyone can post (legacy behavior).
  //   - Sister stores → only SM / DM / admin. Wow crew can VIEW but not
  //     post. We mirror the backend gate in `_save_story_from_data_url` so
  //     the UI doesn't show an entry point that hard-fails on submit.
  const homeStoreId = user?.home_store_id || "store_3094";
  const isFounderStore = homeStoreId === "store_3094";
  const isPrivileged = !!(user?.is_admin || user?.is_manager || user?.is_district_manager);
  const canPost = isFounderStore || isPrivileged;
  // Seed groups synchronously from sessionStorage so the story rings paint on
  // the FIRST render after a reload — no empty-row flash while the network
  // call is in flight. (user report 2026-05-02 "stories populate slowly")
  // IMPORTANT: filter out EXPIRED cached stories so we never try to react to
  // or view a story that's already 24h+ old. Otherwise the tap hits a 404
  // from the server and users see "Reaction not sent" / "Couldn't load story"
  // errors. (user report 2026-05-02)
  const cacheKey = user?.crew_id ? `stories:${user.crew_id}` : "";
  const [groups, setGroups] = useState(() => {
    if (!enabled || !cacheKey) return [];
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (!cached) return [];
      const list = JSON.parse(cached);
      const nowIso = new Date().toISOString();
      const fresh = (list || []).filter((s) => !s.expires_at || s.expires_at > nowIso);
      if (fresh.length !== (list || []).length) {
        // Rewrite the cache with only fresh entries so subsequent reads are consistent.
        try { sessionStorage.setItem(cacheKey, JSON.stringify(fresh)); } catch { /* ignore */ }
      }
      return groupByAuthor(fresh);
    } catch { /* ignore */ }
    return [];
  });
  const [internalComposerOpen, setInternalComposerOpen] = useState(false);
  // The composer is controllable from the outside (ProfileMenuButton on the
  // home header) OR self-managed (when the row is standalone). Prefer the
  // external state when the parent wired it up.
  const composerOpen = externalComposerOpen !== undefined ? externalComposerOpen : internalComposerOpen;
  const setComposerOpen = (v) => {
    if (onComposerOpenChange) onComposerOpenChange(v);
    else setInternalComposerOpen(v);
  };
  const [viewerState, setViewerState] = useState(null); // { groupIndex, storyIndex }

  const fetchStories = useCallback(async () => {
    if (!enabled) { setGroups([]); return; }
    try {
      const res = await axios.get(`${API}/stories?viewer_id=${user.crew_id}`);
      const list = res.data || [];
      setGroups(groupByAuthor(list));
      if (cacheKey) {
        try { sessionStorage.setItem(cacheKey, JSON.stringify(list)); } catch { /* quota — ignore */ }
      }
    } catch (e) {
      console.error("stories fetch", e);
    }
  }, [user.crew_id, enabled, cacheKey]);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchStories();
    // Only poll while the tab is visible — mirrors the Inbox visibility-aware
    // polling. Prevents stacked requests on mobile after backgrounding.
    let id = null;
    const start = () => { if (!id) id = setInterval(fetchStories, 30000); };
    const stop = () => { if (id) { clearInterval(id); id = null; } };
    const onVis = () => {
      if (document.hidden) stop();
      else { fetchStories(); start(); }
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchStories, enabled]);

  // Optimistic insert after a post — splice the fresh story doc into our
  // groups state so the ring appears INSTANTLY, then run a background fetch
  // to sync with the server's canonical view. Avoids the 200-500ms network
  // lag where users would post → wait → see their ring pop in.
  const insertPostedStory = (newStory) => {
    if (!newStory || !newStory.id) { fetchStories(); return; }
    setGroups((prev) => {
      // Route store-persona posts into the shared __store__ bucket, not the
      // real poster's personal bucket. Otherwise the poster sees the story
      // under their own ring on the home row even though it was posted
      // "as the Store". (user report 2026-05-04)
      const bucketId = newStory.as_store ? STORE_GROUP_ID : user.crew_id;
      const existing = prev.findIndex((g) => g.author_id === bucketId);
      if (existing >= 0) {
        // Bucket already exists — prepend the new story.
        const next = [...prev];
        next[existing] = {
          ...next[existing],
          stories: [newStory, ...next[existing].stories],
        };
        return next;
      }
      // New bucket.
      return [
        {
          author_id: bucketId,
          author_name: newStory.author_name || user.first_name || "",
          author_photo: newStory.author_photo || user.photo_url || "",
          stories: [newStory],
          is_store: bucketId === STORE_GROUP_ID,
        },
        ...prev,
      ];
    });
    // Refresh from server in the background so other posters / view counts
    // come back in sync.
    fetchStories();
  };

  // Feature toggle: admin disabled stories → hide the entire row, no entry
  // point, no fetch. Profile highlights stay accessible separately.
  if (!enabled) return null;

  // No post button (sister-store wow crew) + no rings visible → hide the
  // entire row. Otherwise we'd render an empty horizontal scroller. Once
  // the SM posts a story it'll surface here automatically on the next poll.
  if (!canPost && groups.length === 0) return null;

  // Keep the "Your story" add-button ALWAYS visible (even when no one has
  // posted yet) so crew have an obvious entry point to share their first
  // story of the day. Previously the entire row was hidden when empty,
  // which made the feature feel invisible. The row only disappears when
  // admin explicitly disables Stories in Settings → Features.
  // (user ask 2026-02-22 "the stories icon needs to stay visible, only
  // disappear when admin turns them off in the setting tab")

  // Sort groups: my own story first (if any), then most-recently-posted.
  const sorted = [...groups].sort((a, b) => {
    if (a.author_id === user.crew_id) return -1;
    if (b.author_id === user.crew_id) return 1;
    return new Date(b.stories[0].created_at) - new Date(a.stories[0].created_at);
  });

  const myGroup = sorted.find((g) => g.author_id === user.crew_id);

  // "Add your first story!" nudge — a gentle pill above the ring on days
  // where the crew member hasn't posted yet. Dismisses the moment they tap
  // the ring (or post), and self-suppresses for the rest of the MST day via
  // a per-crew localStorage flag so we don't nag. (user ask 2026-02-22.
  // Timezone fix 2026-05-25: was using UTC date which flipped 5+ hours
  // before midnight MST — would re-show the nudge to a crew that already
  // dismissed it earlier the same day.)
  const todayKey = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
  const nudgeDismissKey = user?.crew_id ? `storyNudgeDismissed:${user.crew_id}:${todayKey}` : "";
  const nudgeDismissed = (() => {
    try { return nudgeDismissKey && localStorage.getItem(nudgeDismissKey) === "1"; } catch { return false; }
  })();
  const showFirstStoryNudge = !myGroup && !nudgeDismissed;
  const dismissNudge = () => {
    try { if (nudgeDismissKey) localStorage.setItem(nudgeDismissKey, "1"); } catch { /* ignore */ }
  };

  return (
    <>
      <div
        className="mb-3 flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide"
        data-testid="stories-row"
        style={{ scrollbarWidth: "none" }}
      >
        {canPost && (
        <button
          type="button"
          onClick={() => {
            dismissNudge();
            // If the viewer already has an active story, tap opens the viewer;
            // the small "+" badge in the corner is the dedicated "post new"
            // affordance (stopPropagation'd below). Matches Instagram: tap
            // your own ring → see your story, tap the "+" → add another.
            if (myGroup) {
              const gi = sorted.findIndex((g) => g.author_id === user.crew_id);
              setViewerState({ groupIndex: gi >= 0 ? gi : 0, storyIndex: 0 });
            } else {
              setComposerOpen(true);
            }
          }}
          className="flex-shrink-0 flex flex-col items-center gap-1 focus:outline-none group relative"
          data-testid="story-add-button"
          aria-label={myGroup ? "View your story" : "Post a story"}
        >
          {showFirstStoryNudge && (
            <span
              className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-600 text-white text-[10px] font-extrabold px-2.5 py-1 shadow-md pointer-events-none z-10 story-nudge-float"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
              data-testid="story-first-nudge"
            >
              Add your first story!
              <span
                className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0"
                style={{
                  borderLeft: "5px solid transparent",
                  borderRight: "5px solid transparent",
                  borderTop: "5px solid #2563eb",
                }}
              />
            </span>
          )}
          <div className="relative w-[62px] h-[62px]">
            <div className={`w-full h-full rounded-full flex items-center justify-center overflow-hidden ${myGroup ? "p-[2px] story-gradient-ring" : "bg-slate-100 border-2 border-dashed border-slate-300 group-hover:border-blue-500"} transition-colors`}>
              <div className={`w-full h-full rounded-full overflow-hidden flex items-center justify-center ${myGroup ? "bg-white p-[2px]" : ""}`}>
                <div className="w-full h-full rounded-full overflow-hidden bg-slate-100 flex items-center justify-center">
                  {myGroup ? (
                    <img
                      src={myGroup.author_photo || user?.photo_url || (user?.crew_id ? `${API}/crew/${user.crew_id}/avatar` : "")}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // If the stored author_photo 404s, fall back to the
                        // lazy crew avatar endpoint so the ring never renders
                        // empty (user report 2026-08-30: "avatar gone from
                        // my story"). Bump the src only once to avoid a
                        // reload loop when the crew truly has no avatar.
                        const fallback = user?.crew_id ? `${API}/crew/${user.crew_id}/avatar` : "";
                        if (fallback && e.currentTarget.src.indexOf("/api/crew/") === -1) {
                          e.currentTarget.src = fallback;
                        }
                      }}
                    />
                  ) : (
                    // No story yet — still show the crew's real avatar in the
                    // dashed "Your Story" bubble instead of just their initial.
                    // Bug 2026-08-30: header shows Dom's photo but this bubble
                    // was rendering "D". Root cause: `user.photo_url` from
                    // AuthContext is `null` for some crew whose avatar only
                    // lives at the lazy `/api/crew/:id/avatar` endpoint. Add
                    // the same fallback chain used in the `myGroup` branch.
                    (user?.photo_url || user?.crew_id) ? (
                      <img
                        src={user?.photo_url || `${API}/crew/${user.crew_id}/avatar`}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const fallback = user?.crew_id ? `${API}/crew/${user.crew_id}/avatar` : "";
                          if (fallback && e.currentTarget.src.indexOf("/api/crew/") === -1) {
                            e.currentTarget.src = fallback;
                          } else {
                            // Both failed → hide the img so the parent
                            // bubble's initials span becomes visible.
                            e.currentTarget.style.display = "none";
                            const sib = e.currentTarget.nextElementSibling;
                            if (sib) sib.style.display = "flex";
                          }
                        }}
                      />
                    ) : null
                  )}
                  {!myGroup && (
                    <span
                      className="text-lg font-bold text-slate-400"
                      style={{ display: (user?.photo_url || user?.crew_id) ? "none" : "flex" }}
                      data-testid="your-story-initials-fallback"
                    >
                      {initialsOf(user?.first_name)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* "+" badge — visible the moment the crew already has at least one
                story, so they can post another WITHOUT having to navigate
                through the profile menu. Sits OUTSIDE the gradient ring (on
                the parent wrapper) so it doesn't inherit the rotation animation
                — addresses 2026-04-21 feedback ("badge spinning felt off")
                while restoring the discoverability we lost. Stops propagation
                so tapping the badge opens the composer instead of opening
                the viewer behind it. (user report 2026-02 "cannot post more
                than 4 stories at a time" — actually a discoverability bug:
                after the first story the ring opens the viewer, leaving the
                Profile-menu path as the only way to add another.) */}
            {myGroup && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); dismissNudge(); setComposerOpen(true); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    dismissNudge();
                    setComposerOpen(true);
                  }
                }}
                className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-blue-600 hover:bg-blue-700 border-2 border-white flex items-center justify-center shadow cursor-pointer z-10 transition-colors"
                aria-label="Post another story"
                data-testid="story-add-another"
              >
                <span className="text-white font-black text-[12px] leading-none">+</span>
              </span>
            )}
          </div>
          <span className="text-[10px] font-bold text-slate-600 leading-tight max-w-[64px] truncate">
            {myGroup ? "Your story" : "Your story"}
          </span>
        </button>
        )}

        {sorted.filter((g) => g.author_id !== user.crew_id || !myGroup).map((g, idx) => {
          // Highlight with gradient ring if the viewer hasn't seen all of
          // this author's stories yet (matches Instagram's unread behavior).
          const allSeen = g.stories.every((s) => s.already_viewed);
          const groupIdxInSorted = sorted.findIndex((x) => x.author_id === g.author_id);
          return (
            <button
              key={g.author_id}
              type="button"
              onClick={() => setViewerState({ groupIndex: groupIdxInSorted, storyIndex: 0 })}
              className="flex-shrink-0 flex flex-col items-center gap-1 focus:outline-none"
              data-testid={`story-ring-${g.author_id}`}
            >
              <div
                className={`p-[2px] rounded-full ${allSeen ? "bg-slate-200" : "story-gradient-ring"}`}
              >
                <div className="w-[58px] h-[58px] rounded-full bg-white p-[2px]">
                  <div className="w-full h-full rounded-full overflow-hidden bg-slate-100 flex items-center justify-center">
                    {g.author_photo ? (
                      <img src={g.author_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-base font-bold text-slate-400">{initialsOf(g.author_name)}</span>
                    )}
                  </div>
                </div>
              </div>
              <span className="text-[10px] font-bold text-slate-600 leading-tight max-w-[64px] truncate">
                {g.author_id === user.crew_id ? "You" : g.author_name}
                {!g.is_store && g.stories[0]?.home_store_code && (
                  <span className="block text-[9px] font-semibold text-slate-400" data-testid={`story-ring-store-${g.stories[0].home_store_code}`}>
                    · {g.stories[0].home_store_code}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {composerOpen && (
        <StoryComposer
          user={user}
          onClose={() => setComposerOpen(false)}
          onPosted={(newStory) => { setComposerOpen(false); insertPostedStory(newStory); }}
        />
      )}

      {viewerState && (
        <StoryViewer
          user={user}
          isManager={isManager}
          groups={sorted}
          initial={viewerState}
          onClose={() => setViewerState(null)}
          onMutated={fetchStories}
          onPostAnother={() => { setViewerState(null); setComposerOpen(true); }}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Composer modal                                                      */
/* ------------------------------------------------------------------ */
function StoryComposer({ user, onClose, onPosted }) {
  // `mediaBlob` is the actual bytes we'll POST via multipart/form-data. We
  // keep this as a Blob (not a base64 string) so iOS Safari doesn't blow up
  // on the 13 MB in-memory JSON clone axios makes before sending. `preview`
  // is a short-lived object URL for the composer preview — revoked when we
  // unmount or swap media.
  const [mediaBlob, setMediaBlob] = useState(null);
  const [preview, setPreview] = useState("");
  const [mediaType, setMediaType] = useState("image");
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);
  // Store-persona toggle — visible only to admin/managers when a store doc
  // exists for the poster. Server still re-validates on POST so a tampered
  // client can't bypass the role gate. (user request 2026-05-04)
  const [asStore, setAsStore] = useState(false);
  // @mention autocomplete state (2026-06-15 user ask "add mentions to
  // stories"). `mentionQuery` is null when not autocompleting, otherwise
  // the text after the @ that's matched against crew first names.
  const [crewList, setCrewList] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const captionRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const sid = (typeof window !== "undefined" && window.__wowActiveStoreId)
      || user?.home_store_id || "";
    // Store roster + the district's leaders (incl. the District Manager, who
    // isn't tied to one store) so a DM can be @mentioned in a story.
    Promise.all([
      axios.get(`${API}/crew`, { params: { store_id: sid } }).then((r) => r.data || []).catch(() => []),
      user?.crew_id
        ? axios.get(`${API}/crew/district-leaders`, { params: { actor_id: user.crew_id } })
            .then((r) => r.data || []).catch(() => [])
        : Promise.resolve([]),
    ]).then(([roster, leaders]) => {
      if (cancelled) return;
      const byId = new Map();
      [...roster, ...leaders].forEach((c) => {
        if (c && c.active !== false && c.id !== user?.crew_id) byId.set(c.id, c);
      });
      setCrewList(Array.from(byId.values()));
    });
    return () => { cancelled = true; };
  }, [user?.crew_id, user?.home_store_id]);

  const onCaptionChange = (e) => {
    const next = e.target.value.slice(0, 280);
    setCaption(next);
    const cursor = e.target.selectionStart ?? next.length;
    const upToCursor = next.slice(0, cursor);
    const m = upToCursor.match(/(^|\s)@([A-Za-z]*)$/);
    setMentionQuery(m ? m[2] : null);
  };

  const insertMention = (crewMember) => {
    const input = captionRef.current;
    if (!input) return;
    const cursor = input.selectionStart ?? caption.length;
    const upToCursor = caption.slice(0, cursor);
    const m = upToCursor.match(/(^|\s)@([A-Za-z]*)$/);
    if (!m) return;
    const start = upToCursor.length - m[2].length; // position right after the @
    const newValue = caption.slice(0, start) + crewMember.first_name + " " + caption.slice(cursor);
    setCaption(newValue.slice(0, 280));
    setMentionQuery(null);
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + crewMember.first_name.length + 1;
      try { input.setSelectionRange(pos, pos); } catch { /* ignore */ }
    });
  };

  const mentionMatches = (() => {
    if (mentionQuery === null) return [];
    const q = (mentionQuery || "").toLowerCase();
    return crewList
      .filter((c) => (c.first_name || "").toLowerCase().startsWith(q))
      .slice(0, 5);
  })();
  // Resolve the poster's HOME STORE so the "Post as ..." label reflects the
  // actual store the SM works at — not the global singleton (which always
  // showed "3094 - Wheat Ridge"). Bug fix 2026-05-23.
  const [homeStore, setHomeStore] = useState(null);
  useEffect(() => {
    if (!user?.home_store_id || !user?.crew_id) return;
    let cancelled = false;
    axios
      .get(`${API}/stores/${user.home_store_id}`, { params: { actor_id: user.crew_id } })
      .then((r) => { if (!cancelled) setHomeStore(r.data); })
      .catch(() => { /* silent — falls back to the generic "Store" label */ });
    return () => { cancelled = true; };
  }, [user?.home_store_id, user?.crew_id]);
  const storeDisplayName = (() => {
    if (!homeStore) return "";
    const override = (homeStore.store_account_name || "").trim();
    if (override) return override;
    const code = homeStore.code || "";
    const name = homeStore.name || "";
    if (code && name) return `${code} · ${name}`;
    return code || name || "";
  })();
  const storeReady = !!(
    (user?.is_admin || user?.is_manager || user?.is_district_manager) &&
    storeDisplayName
  );
  const fileRef = useRef(null);

  // Revoke the preview object URL on unmount so we don't leak memory (matters
  // on long-lived tabs — iOS Safari holds the whole blob until GC).
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        try { URL.revokeObjectURL(preview); } catch { /* ignore */ }
      }
    };
  }, [preview]);

  const clearMedia = () => {
    if (preview && preview.startsWith("blob:")) {
      try { URL.revokeObjectURL(preview); } catch { /* ignore */ }
    }
    setMediaBlob(null);
    setPreview("");
    setMediaType("image");
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      if (f.type.startsWith("video/")) {
        const { blob, previewUrl } = await validateVideo(f);
        if (preview && preview.startsWith("blob:")) {
          try { URL.revokeObjectURL(preview); } catch { /* ignore */ }
        }
        setMediaBlob(blob);
        setPreview(previewUrl);
        setMediaType("video");
      } else {
        const { blob, previewUrl } = await compressImage(f);
        if (preview && preview.startsWith("blob:")) {
          try { URL.revokeObjectURL(preview); } catch { /* ignore */ }
        }
        setMediaBlob(blob);
        setPreview(previewUrl);
        setMediaType("image");
      }
    } catch (err) {
      toast.error(err.message || "Couldn't read media");
    }
  };

  const post = async () => {
    if (!mediaBlob) { toast.error("Pick a photo or video first"); return; }
    setSaving(true);
    try {
      // Multipart upload — iOS Safari ships raw bytes reliably. The server
      // base64-encodes into the canonical data URL shape on arrival so the
      // existing `/stories` JSON path stays unchanged for older clients.
      const form = new FormData();
      form.append("author_id", user.crew_id);
      form.append("caption", caption);
      form.append("media_type", mediaType);
      form.append("as_store", String(asStore && storeReady));
      // Give the file a predictable name — iOS sometimes sends empty name.
      const ext = mediaType === "video" ? "mp4" : "jpg";
      form.append("file", mediaBlob, mediaBlob.name || `story.${ext}`);
      const res = await axios.post(`${API}/stories/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        // 90s timeout — covers slow store wifi on the Zebra scanners without
        // hanging forever if the user backgrounds the tab.
        timeout: 90000,
      });
      toast.success("Story posted — it'll disappear in 24h");
      onPosted(res.data);
    } catch (e) {
      // Surface a specific reason whenever the server gave one; otherwise
      // distinguish true network errors from silent rejects so the user
      // knows what to try next. (user report 2026-05-02 "Post failed" with
      // no detail was actually iOS dropping the XHR on big payloads.)
      const serverDetail = e.response?.data?.detail;
      let msg = serverDetail;
      if (!msg) {
        if (e.code === "ECONNABORTED") msg = "Upload timed out — check your wifi and try again.";
        else if (!e.response) msg = "Network error — couldn't reach the server.";
        else msg = `Post failed (${e.response.status})`;
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Modernized 2026-05-12 per user feedback "huge and outdated".
  // New shape: bottom-sheet on mobile (slides up, max 88vh), centered
  // dialog on desktop. The empty-state picker is a compact ~240px tile
  // — only when a photo/video is loaded does the preview expand to the
  // taller aspect ratio. Smoother colors, drag-handle indicator, no
  // more wall-of-white feeling.
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 story-composer-overlay"
      onClick={onClose}
      data-testid="story-composer"
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col max-h-[88vh] shadow-2xl story-composer-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile-only visual cue that this is a sheet) */}
        <div className="flex justify-center pt-2 pb-1 sm:hidden">
          <span className="block w-10 h-1 rounded-full bg-slate-300" aria-hidden="true" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-2 pb-3 sm:px-5 sm:pt-4">
          <div className="flex items-center gap-2.5">
            {user?.photo_url ? (
              <img src={user.photo_url} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-blue-100" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-black text-sm">
                {(user?.first_name || "?")[0]}
              </div>
            )}
            <div className="leading-tight">
              <h3 className="font-black text-base">Share a story</h3>
              <p className="text-[11px] text-slate-500">Visible to your team for 24h</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
            data-testid="story-composer-close"
            aria-label="Close composer"
          >
            <X weight="bold" className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 sm:px-5 pb-3 flex flex-col gap-3 overflow-auto">
          <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={onFile} />
          {mediaBlob ? (
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-slate-900">
              {mediaType === "video" ? (
                <video src={preview} className="w-full h-full object-cover" muted autoPlay loop playsInline />
              ) : (
                <img src={preview} alt="preview" className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={clearMedia}
                className="absolute top-2.5 right-2.5 bg-black/60 backdrop-blur-sm text-white rounded-full p-2 hover:bg-black/80"
                aria-label="Remove media"
              >
                <X weight="bold" className="w-4 h-4" />
              </button>
              <span className="absolute bottom-2.5 left-2.5 bg-black/60 backdrop-blur-sm text-white text-[10px] font-black rounded-full px-2.5 py-1 uppercase tracking-wider">
                {mediaType === "video" ? "Video · 10s max" : "Photo"}
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="relative rounded-2xl overflow-hidden h-44 sm:h-52 flex flex-col items-center justify-center gap-1.5 text-white font-bold text-sm transition-all hover:scale-[1.01] active:scale-[0.99] group"
              style={{ background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)" }}
              data-testid="story-pick-photo"
            >
              {/* Subtle dot-pattern overlay for that fun "add new" vibe */}
              <span
                aria-hidden="true"
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
                  backgroundSize: "16px 16px",
                }}
              />
              <span className="relative w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-1 group-hover:bg-white/30 transition-colors">
                <Camera weight="fill" className="w-6 h-6 text-white" />
              </span>
              <span className="relative font-black text-base">Pick a photo or video</span>
              <span className="relative text-[11px] font-medium text-white/80">Photo up to 12MB · Video up to 10s / 11MB</span>
            </button>
          )}
          <div className="relative">
            <textarea
              ref={captionRef}
              value={caption}
              onChange={onCaptionChange}
              placeholder="Add a caption… try @name to tag someone"
              rows={2}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:border-blue-400 focus:bg-white transition-colors placeholder:text-slate-400"
              data-testid="story-caption"
            />
            {/* @mention picker — floats below the caption when typing @ */}
            {mentionQuery !== null && mentionMatches.length > 0 && (
              <div
                className="absolute left-0 right-0 top-full mt-1 z-10 bg-white border border-slate-200 rounded-xl shadow-lg max-h-44 overflow-y-auto"
                data-testid="story-mention-picker"
              >
                {mentionMatches.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => insertMention(c)}
                    className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-50 active:bg-slate-100"
                    data-testid={`story-mention-option-${c.id}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center text-[10px] font-black text-slate-700">
                      {c.photo_url
                        ? <img src={c.photo_url} alt={c.first_name} className="w-full h-full object-cover" />
                        : (c.first_name || "?").charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-bold text-slate-900">{c.first_name} {c.last_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between -mt-1">
            <span className="text-[11px] text-slate-400">{caption.length}/280</span>
          </div>
          {/* Post-as-store toggle — only for admin/managers when a home store doc resolved */}
          {storeReady && (
            <label className="flex items-center gap-2.5 text-xs font-semibold text-slate-700 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl px-3 py-2.5" data-testid="story-as-store">
              <input type="checkbox" checked={asStore} onChange={(e) => setAsStore(e.target.checked)} className="w-4 h-4 accent-blue-600" />
              <Storefront weight="fill" className="w-4 h-4 text-blue-600" />
              <span className="leading-tight">
                Post as <span className="font-black text-blue-700">{storeDisplayName}</span>
                <span className="block text-[10px] text-slate-500 font-normal mt-0.5">Audit trail still records your name.</span>
              </span>
            </label>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-4 sm:px-5 pt-2 pb-3 sm:pb-4 flex gap-2.5 border-t border-slate-100 bg-white/95 backdrop-blur"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        >
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={post}
            disabled={saving || !mediaBlob}
            className="flex-1 h-11 rounded-full text-white text-sm font-black disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition-all shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 disabled:shadow-none"
            style={{ background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)" }}
            data-testid="story-post"
          >
            <PaperPlaneRight weight="fill" className="w-4 h-4" /> {saving ? "Posting…" : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Highlight collection picker — modal popped when the author taps the */
/* "Save to Highlights" star. Lets them choose an existing bucket OR   */
/* create a new one inline. Max 6 collections per crew (server-enforced). */
/* ------------------------------------------------------------------ */

function HighlightCollectionPicker({ user, storyId, onClose, onSaved }) {
  const [collections, setCollections] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.crew_id) return;
    axios.get(`${API}/crew/${user.crew_id}/highlight-collections`)
      .then((r) => setCollections(r.data?.collections || []))
      .catch(() => {});
  }, [user?.crew_id]);

  const save = async (target) => {
    setSaving(true);
    try {
      const body = { crew_id: user.crew_id };
      if (target?.id) body.collection_id = target.id;
      else if (target?.name) body.collection_name = target.name;
      await axios.post(`${API}/stories/${storyId}/highlight`, body);
      toast.success(target?.name ? `Saved to "${target.name}"` : "Saved to highlights");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="highlight-picker"
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-4 pb-2 border-b border-slate-100">
          <h3 className="text-base font-black tracking-tight" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            Save to which collection?
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Pick an existing one or create a new bucket.</p>
        </div>

        <div className="px-3 py-3 max-h-[55vh] overflow-y-auto">
          {/* Always offer the "All / Uncategorized" target so users can save
              without classifying. Lands the highlight with no collection_id. */}
          <button
            type="button"
            onClick={() => save({ id: "" })}
            disabled={saving}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 disabled:opacity-50 text-left"
            data-testid="highlight-pick-uncategorized"
          >
            <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
              <FolderSimpleStar weight="fill" className="w-5 h-5 text-slate-600" />
            </span>
            <span className="flex-1">
              <p className="text-sm font-bold">All / Uncategorized</p>
              <p className="text-xs text-slate-500">Default — appears in your main highlights row.</p>
            </span>
          </button>

          {collections.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => save({ id: c.id, name: c.name })}
              disabled={saving}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 disabled:opacity-50 text-left"
              data-testid={`highlight-pick-${c.id}`}
            >
              <span className="w-9 h-9 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <Star weight="fill" className="w-5 h-5 text-yellow-600" />
              </span>
              <span className="flex-1">
                <p className="text-sm font-bold">{c.name}</p>
                <p className="text-xs text-slate-500">{c.count} highlight{c.count === 1 ? "" : "s"}</p>
              </span>
            </button>
          ))}

          {creating ? (
            <div className="mt-2 px-3 py-2 rounded-xl border border-blue-200 bg-blue-50/50">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value.slice(0, 30))}
                placeholder="e.g. Wow Crew Moments"
                className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                autoFocus
                data-testid="highlight-new-name"
              />
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => { setCreating(false); setNewName(""); }}
                  className="flex-1 h-9 rounded-md border border-slate-200 text-xs font-bold text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => save({ name: newName })}
                  disabled={!newName.trim() || saving}
                  className="flex-1 h-9 rounded-md bg-blue-600 text-white text-xs font-bold disabled:opacity-50"
                  data-testid="highlight-create-and-save"
                >
                  Create & Save
                </button>
              </div>
              <p className="mt-1 text-[10px] text-slate-500">{collections.length}/6 collections used.</p>
            </div>
          ) : (
            collections.length < 6 && (
              <button
                type="button"
                onClick={() => setCreating(true)}
                disabled={saving}
                className="w-full mt-1 flex items-center gap-3 px-3 py-2.5 rounded-xl border border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-left"
                data-testid="highlight-new-collection"
              >
                <span className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Plus weight="bold" className="w-5 h-5 text-blue-600" />
                </span>
                <span className="flex-1 text-sm font-bold text-blue-600">Create new collection</span>
              </button>
            )
          )}
        </div>

        <div className="px-3 py-3 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="w-full h-10 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fullscreen viewer with reactions + audience list for author         */
/* ------------------------------------------------------------------ */
function StoryViewer({ user, isManager, groups, initial, onClose, onMutated, onPostAnother }) {
  const [groupIndex, setGroupIndex] = useState(initial.groupIndex);
  const [storyIndex, setStoryIndex] = useState(initial.storyIndex);
  const [story, setStory] = useState(null);
  const [showAudience, setShowAudience] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [savedToHighlights, setSavedToHighlights] = useState(false);
  const [highlightPickerOpen, setHighlightPickerOpen] = useState(false);
  // Videos autoplay muted because browsers block unmuted autoplay. User taps
  // the speaker icon to enable sound; we persist the choice for the rest of
  // the session so they don't have to tap on every video in a row.
  const [muted, setMuted] = useState(() => {
    try { return sessionStorage.getItem("storyVideoUnmuted") !== "1"; } catch { return true; }
  });
  // Direct ref to the <video> element. iOS Safari quirk (user report
  // 2026-05-15 "sound button doesn't work when we post a story with sound"):
  // changing the `muted` React prop alone does NOT unmute a video that
  // autoplayed muted — the audio track stays silent. The fix is to
  // explicitly set `videoEl.muted` AND call `play()` inside the user-gesture
  // click handler so iOS treats it as an authorized unmute.
  const videoRef = useRef(null);
  const toggleMute = () => {
    setMuted((prev) => {
      const next = !prev;
      try { sessionStorage.setItem("storyVideoUnmuted", next ? "0" : "1"); } catch { /* ignore */ }
      // Force the DOM element to honor the change immediately. Without this
      // the React-managed `muted` prop appears to update but iOS keeps the
      // track silent until the user navigates away and back.
      const v = videoRef.current;
      if (v) {
        v.muted = next;
        // Re-issue play() inside the click handler so the new audio state
        // takes effect under the autoplay policy gesture window.
        try { v.play(); } catch { /* ignore — element may not be ready */ }
      }
      return next;
    });
  };

  const group = groups[groupIndex];
  const currentStoryStub = group?.stories?.[storyIndex];
  const storyId = currentStoryStub?.id;
  // Authorship: for normal (personal) groups the bucket id IS the crew_id,
  // so a simple equality check works. For the shared __store__ bucket the
  // group.author_id is a sentinel so we fall back to `posted_by_id` on the
  // story itself — that preserves delete / save-to-highlights / audience
  // access for the real poster while still showing the story in the Store
  // ring on the home row. (user report 2026-05-04)
  const isAuthor = group?.is_store
    ? (currentStoryStub?.posted_by_id === user.crew_id)
    : (group?.author_id === user.crew_id);

  // Load + mark-viewed on each story change. The view endpoint returns the
  // full (author-redacted) story so we can render reaction counts, viewer
  // list etc. without a second call.
  // IMPORTANT: Seed `story` synchronously from the local list stub so the new
  // media renders IMMEDIATELY on tap. Without this seed the previous story's
  // photo/video stays visible until the /view POST round-trips, which reads
  // as the viewer being "stuck" on the prior story. (user report 2026-05-02)
  useEffect(() => {
    if (!storyId) return;
    let cancelled = false;
    setSavedToHighlights(false);
    setReplyText("");
    // Optimistic seed → instant media swap.
    if (currentStoryStub) {
      setStory({
        ...currentStoryStub,
        view_count: currentStoryStub.view_count || 0,
        reaction_counts: currentStoryStub.reaction_counts || {},
      });
    } else {
      setStory(null);
    }
    (async () => {
      try {
        const res = await axios.post(`${API}/stories/${storyId}/view`, null, {
          params: { crew_id: user.crew_id },
        });
        if (!cancelled) setStory(res.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Couldn't load story");
      }
    })();
    return () => { cancelled = true; };
  }, [storyId, user.crew_id]);

  if (!group || !storyId) { onClose(); return null; }

  const goNext = () => {
    if (storyIndex + 1 < group.stories.length) {
      setStoryIndex(storyIndex + 1);
    } else if (groupIndex + 1 < groups.length) {
      setGroupIndex(groupIndex + 1);
      setStoryIndex(0);
    } else {
      onClose();
    }
  };
  const goPrev = () => {
    if (storyIndex > 0) setStoryIndex(storyIndex - 1);
    else if (groupIndex > 0) {
      setGroupIndex(groupIndex - 1);
      const prevLen = groups[groupIndex - 1].stories.length;
      setStoryIndex(prevLen - 1);
    }
  };

  const react = async (emoji) => {
    if (isAuthor) return;
    try {
      const res = await axios.post(`${API}/stories/${storyId}/react`, {
        crew_id: user.crew_id,
        emoji,
      });
      setStory(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reaction failed");
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this story?")) return;
    try {
      await axios.delete(`${API}/stories/${storyId}`, { params: { crew_id: user.crew_id } });
      toast.success("Story deleted");
      onMutated && onMutated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Author saves their own story into permanent highlights. When tapped,
  // we pop the collection-picker modal so they can drop the story into a
  // bucket ("Store Manager Wins", "Wow Crew Moments", etc.). Idempotent —
  // if the story is already highlighted in another collection, the picker
  // will move it. (user spec 2026-02-22 "modal picker before save")
  const onSaveHighlight = () => setHighlightPickerOpen(true);

  // Non-author sends a quick DM reply that references this story.
  const onSendReply = async () => {
    const text = replyText.trim();
    if (!text) return;
    setSending(true);
    try {
      await axios.post(`${API}/stories/${storyId}/reply`, {
        story_id: storyId,
        from_id: user.crew_id,
        message: text,
      });
      toast.success(`Sent to ${group.author_name}`);
      setReplyText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reply failed");
    } finally {
      setSending(false);
    }
  };

  const canDelete = isAuthor || isManager;
  const timeAgo = story?.created_at ? humanTimeAgo(story.created_at) : "";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center story-viewer-overlay"
      style={{ background: "rgba(8, 8, 12, 0.92)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)" }}
      data-testid="story-viewer"
    >
      {/* Desktop close button — floats top-right of the whole overlay so it
          never collides with the in-card chrome. Hidden on mobile because
          the inline close button inside the header is more thumb-reachable. */}
      <button
        onClick={onClose}
        className="hidden sm:flex absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center z-30 backdrop-blur"
        aria-label="Close"
        data-testid="story-viewer-close-desktop"
      >
        <X weight="bold" className="w-5 h-5" />
      </button>

      {/* Card — phone-aspect rounded panel. On mobile it fills the screen,
          on desktop it sits in a 9:16 frame with a glow ring. */}
      <div className="relative w-full h-full sm:w-auto sm:h-[min(92vh,820px)] sm:aspect-[9/16] sm:rounded-3xl overflow-hidden flex flex-col bg-slate-900 sm:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] sm:ring-1 sm:ring-white/10 story-viewer-card">
        {/* Tap-zones for prev/next — mobile only. Moved INSIDE the card on
            2026-05-22 so they share the stacking context of the mute button
            (z-30 inside the card). Previously they lived as siblings of the
            card at the overlay level with z-10, but the card itself had no
            z-index — so the tap zones painted ON TOP of the card's contents
            and intercepted every tap on the right edge, including taps
            meant for the mute speaker icon. User report: "click to unmute
            moves to the next story". */}
        <button onClick={goPrev} aria-label="Previous story" className="absolute left-0 top-0 h-full w-1/4 z-10 sm:hidden" />
        <button onClick={goNext} aria-label="Next story" className="absolute right-0 top-0 h-full w-1/4 z-10 sm:hidden" />
        {/* Progress bars */}
        <div className="absolute top-2.5 left-2.5 right-2.5 flex gap-1 z-20">
          {group.stories.map((_, i) => (
            <div key={i} className="flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden">
              <div
                className={`h-full bg-white transition-all ${
                  i < storyIndex ? "w-full" : i === storyIndex ? "w-full" : "w-0"
                }`}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-6 left-3 right-3 flex items-center gap-2.5 z-20 text-white">
          <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white/40 flex items-center justify-center text-xs font-black bg-white/10">
            {group.author_photo
              ? <img src={group.author_photo} alt="" className="w-full h-full object-cover" />
              : initialsOf(group.author_name)}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-black text-sm truncate">
              {(isAuthor && !group?.is_store) ? "You" : group.author_name}
              {!group?.is_store && story?.home_store_code && (
                <span className="ml-1.5 text-white/70 font-semibold" data-testid="story-viewer-store-code">
                  · {story.home_store_code}
                </span>
              )}
            </span>
            {(story?.as_store && story?.posted_by_name) ? (
              <span className="text-[10px] text-white/70 font-semibold truncate" data-testid="story-posted-by">
                Posted by {story.posted_by_name}
              </span>
            ) : (
              <span className="text-[10px] text-white/60 font-medium">{timeAgo}</span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {isAuthor && !group?.is_store && onPostAnother && (
              <button
                onClick={onPostAnother}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center backdrop-blur text-white"
                aria-label="Post another story"
                title="Post another story"
                data-testid="story-post-another"
              >
                <Plus weight="bold" className="w-4 h-4" />
              </button>
            )}
            {isAuthor && (
              <button
                onClick={onSaveHighlight}
                className={`w-9 h-9 rounded-full flex items-center justify-center backdrop-blur transition-colors ${savedToHighlights ? "bg-yellow-400 text-black" : "bg-white/10 hover:bg-white/25 text-white"}`}
                aria-label={savedToHighlights ? "Saved to highlights" : "Save to highlights"}
                title={savedToHighlights ? "Saved!" : "Save to highlights"}
                data-testid="story-save-highlight"
              >
                <BookmarkSimple weight={savedToHighlights ? "fill" : "bold"} className="w-4 h-4" />
              </button>
            )}
            {canDelete && (
              <button
                onClick={onDelete}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center backdrop-blur"
                aria-label="Delete story"
                data-testid="story-delete-btn"
              >
                <TrashSimple weight="bold" className="w-4 h-4" />
              </button>
            )}
            {/* Mobile-only inline close — desktop uses the outer floating close */}
            <button
              onClick={onClose}
              className="sm:hidden w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center backdrop-blur"
              aria-label="Close"
              data-testid="story-viewer-close"
            >
              <X weight="bold" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Media */}
        {story?.photo_url ? (
          story.media_type === "video" ? (
            <>
              <video
                ref={videoRef}
                key={story.id}
                src={story.photo_url}
                className="w-full h-full object-contain select-none"
                autoPlay
                muted={muted}
                playsInline
                loop
                data-testid="story-video"
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                className="absolute top-[68px] right-3 z-30 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur shadow-lg"
                aria-label={muted ? "Unmute" : "Mute"}
                title={muted ? "Tap to unmute" : "Tap to mute"}
                data-testid="story-mute-toggle"
              >
                {muted ? <SpeakerSlash weight="bold" className="w-5 h-5" /> : <SpeakerHigh weight="bold" className="w-5 h-5" />}
              </button>
            </>
          ) : (
            <img
              src={story.photo_url}
              alt=""
              className="w-full h-full object-contain select-none"
              draggable={false}
            />
          )
        ) : (
          // Lean payload path: the list endpoint omits `photo_url` to keep
          // memory low; we fetch the full doc via POST /stories/{id}/view
          // when the viewer opens. While that request is in flight (usually
          // <300ms) we show a subtle loading shimmer so the screen isn't a
          // blank black rectangle. (2026-05-15 lag fix.)
          <div className="absolute inset-0 flex items-center justify-center" data-testid="story-loading">
            <div className="w-10 h-10 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          </div>
        )}

        {/* Caption — pinned just above the bottom bar with a gradient fade
            for legibility. Bottom offset auto-adapts to whether the
            reactions row is showing. */}
        {story?.caption && (
          <div
            className="absolute left-0 right-0 z-10 px-3 pb-2 pt-12"
            style={{
              bottom: `calc(env(safe-area-inset-bottom, 0px) + ${isAuthor ? 84 : story?.my_reaction ? 84 : 148}px)`,
              background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)",
            }}
          >
            <p className="text-white text-sm font-medium leading-snug">
              {story.caption.split(/(@[A-Za-z][A-Za-z0-9'\-]{0,30})/g).map((part, i) => (
                /^@/.test(part)
                  ? <span key={i} className="font-black text-blue-300" data-testid="story-mention-pill">{part}</span>
                  : <span key={i}>{part}</span>
              ))}
            </p>
          </div>
        )}

        {/* Bottom bar */}
        {isAuthor ? (
          <div
            className="absolute left-3 right-3 z-20"
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
          >
            <button
              onClick={() => setShowAudience(true)}
              className="w-full h-11 rounded-full bg-white/15 backdrop-blur text-white text-xs font-bold flex items-center justify-center gap-2 hover:bg-white/25 transition-colors"
              data-testid="story-audience-btn"
            >
              <Eye weight="fill" className="w-4 h-4" />
              Seen by {story?.view_count || 0}
              {story?.reactors?.length > 0 && (
                <span className="ml-1 opacity-80">· {story.reactors.length} reacted</span>
              )}
            </button>
          </div>
        ) : (
          <div
            className="absolute left-3 right-3 z-20 space-y-2"
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
          >
            {!story?.my_reaction && (
              <div className="flex items-center justify-around gap-2 bg-white/12 backdrop-blur-md rounded-full px-3 py-1.5 ring-1 ring-white/15">
                {TAPBACKS.map((e) => (
                  <button
                    key={e}
                    onClick={() => react(e)}
                    className="text-2xl transition-transform hover:scale-125 active:scale-110 leading-none"
                    data-testid={`story-react-${e}`}
                    aria-label={`React ${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
            {story?.my_reaction && (
              <button
                onClick={() => react(story.my_reaction)}
                className="flex items-center gap-2 bg-white/12 backdrop-blur-md rounded-full px-3.5 py-1.5 text-white text-xs font-bold hover:bg-white/25 ring-1 ring-white/15"
                data-testid="story-reaction-clear"
                aria-label="Undo reaction"
              >
                <span className="text-base leading-none">{story.my_reaction}</span>
                <span className="opacity-80">Undo reaction</span>
              </button>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onSendReply(); }}
                placeholder={`Reply to ${group.author_name}…`}
                className="flex-1 h-11 rounded-full bg-white/10 backdrop-blur-md ring-1 ring-white/15 px-4 text-sm text-white placeholder:text-white/60 focus:outline-none focus:bg-white/20 focus:ring-white/30 transition-all"
                style={{ WebkitTextFillColor: "#fff", colorScheme: "dark" }}
                data-testid="story-reply-input"
              />
              <button
                onClick={onSendReply}
                disabled={sending || !replyText.trim()}
                className="h-11 w-11 rounded-full text-white flex items-center justify-center disabled:opacity-40 shadow-lg shadow-blue-500/40 transition-all"
                style={{ background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)" }}
                aria-label="Send reply"
                data-testid="story-reply-send"
              >
                <PaperPlaneTilt weight="fill" className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop side-nav buttons — floating left/right of the card so users
          who don't think to tap the edge of the photo can still navigate. */}
      <button
        onClick={goPrev}
        className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center backdrop-blur z-30"
        aria-label="Previous story"
        data-testid="story-prev-desktop"
      >
        <CaretLeft weight="bold" className="w-5 h-5" />
      </button>
      <button
        onClick={goNext}
        className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center backdrop-blur z-30"
        aria-label="Next story"
        data-testid="story-next-desktop"
      >
        <CaretRight weight="bold" className="w-5 h-5" />
      </button>

      {/* Audience sheet (author only) */}
      {showAudience && isAuthor && story && (
        <div className="absolute inset-0 z-30 bg-black/60 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setShowAudience(false)}>
          <div
            className="w-full sm:max-w-md mx-auto bg-white rounded-t-3xl sm:rounded-3xl max-h-[70vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="story-audience-sheet"
          >
            <div className="flex justify-center pt-2 pb-1 sm:hidden">
              <span className="block w-10 h-1 rounded-full bg-slate-300" />
            </div>
            <div className="px-4 sm:px-5 pt-2 pb-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-black text-base">Your audience</h3>
                <p className="text-[11px] text-slate-500">{story.view_count || 0} viewers · {story.reactors?.length || 0} reactions</p>
              </div>
              <button onClick={() => setShowAudience(false)} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
                <X weight="bold" className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 overflow-auto space-y-1">
              {(story.viewers || []).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-6">No views yet. Share it in a blast!</p>
              )}
              {(story.viewers || []).map((v) => {
                const r = (story.reactors || []).find((x) => x.crew_id === v.crew_id);
                // Build the "— SM 3015 / DM" suffix from backend-provided
                // fields. Falls back to nothing if the maps weren't built
                // (older clients pre-2026-05-26 won't have these — UI
                // gracefully degrades to name-only).
                const roleBits = [];
                if (v.crew_position_abbr) roleBits.push(v.crew_position_abbr);
                if (v.crew_store_code) roleBits.push(v.crew_store_code);
                const roleLabel = roleBits.join(" ");
                return (
                  <div
                    key={v.crew_id}
                    className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-slate-50"
                    data-testid={`story-viewer-${v.crew_id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center text-xs font-bold text-slate-400 ring-2 ring-white">
                      {v.crew_photo
                        ? <img src={v.crew_photo} alt="" className="w-full h-full object-cover" />
                        : initialsOf(v.crew_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">
                        {v.crew_name}
                        {roleLabel && (
                          <span className="ml-1.5 text-[11px] font-bold text-slate-500" data-testid={`story-viewer-role-${v.crew_id}`}>
                            — {roleLabel}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-slate-500">{humanTimeAgo(v.viewed_at)}</p>
                    </div>
                    {r && <span className="text-xl">{r.emoji}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {highlightPickerOpen && (
        <HighlightCollectionPicker
          user={user}
          storyId={storyId}
          onClose={() => setHighlightPickerOpen(false)}
          onSaved={() => setSavedToHighlights(true)}
        />
      )}
    </div>
  );
}

// Short relative time like "3h" / "just now" — good enough for story chrome.
function humanTimeAgo(iso) {
  if (!iso) return "";
  const delta = (Date.now() - new Date(iso).getTime()) / 1000;
  if (delta < 60) return "just now";
  if (delta < 3600) return `${Math.floor(delta / 60)}m`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h`;
  return `${Math.floor(delta / 86400)}d`;
}
